// src/components/ui/calendar/events/EventStatusBadge/EventStatusBadgeShadcn.jsx
import React from 'react';
import {
  Clock,
  CheckCircle,
  Play,
  AlertTriangle,
  XCircle
} from 'lucide-react';

// shadcn components
import { Badge } from '../../../badge';

import { cn } from '../../../../../utils/cn';

const EventStatusBadgeShadcn = ({
  status,
  size = 'sm', // 'xs', 'sm', 'md'
  variant = 'default', // 'default', 'minimal', 'icon-only'
  className = ''
}) => {
  // Status configurations (matching your current event status system)
  const statusConfig = {
    pending: {
      icon: Clock,
      label: 'Pending',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20',
      borderColor: 'border-amber-500/30'
    },
    confirmed: {
      icon: CheckCircle,
      label: 'Confirmed',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
      borderColor: 'border-blue-500/30'
    },
    in_progress: {
      icon: Play,
      label: 'In Progress',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/20',
      borderColor: 'border-purple-500/30'
    },
    requires_followup: {
      icon: AlertTriangle,
      label: 'Follow-up',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/20',
      borderColor: 'border-orange-500/30'
    },
    completed: {
      icon: CheckCircle,
      label: 'Completed',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20',
      borderColor: 'border-emerald-500/30'
    },
    cancelled: {
      icon: XCircle,
      label: 'Cancelled',
      color: 'text-red-400',
      bgColor: 'bg-red-500/20',
      borderColor: 'border-red-500/30'
    }
  };

  // Size configurations
  const sizeConfig = {
    xs: {
      container: 'px-1.5 py-0.5 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1'
    },
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1.5'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      gap: 'gap-2'
    }
  };

  const config = statusConfig[status];
  const currentSize = sizeConfig[size];

  if (!config) {
    return null;
  }

  const IconComponent = config.icon;

  // Variant-specific rendering
  if (variant === 'icon-only') {
    return (
      <div
        className={cn(
          "inline-flex items-center justify-center rounded-full",
          currentSize.container,
          config.bgColor,
          config.borderColor,
          "border",
          className
        )}
        title={config.label}
      >
        <IconComponent className={cn(currentSize.icon, config.color)} />
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <span
        className={cn(
          "inline-flex items-center font-medium",
          currentSize.gap,
          config.color,
          className
        )}
      >
        <IconComponent className={currentSize.icon} />
        <span>{config.label}</span>
      </span>
    );
  }

  // Default variant with full styling using Badge
  return (
    <Badge
      variant="outline"
      className={cn(
        "inline-flex items-center rounded-full font-medium transition-all duration-200",
        currentSize.container,
        currentSize.gap,
        config.color,
        config.bgColor,
        config.borderColor,
        className
      )}
    >
      <IconComponent className={currentSize.icon} />
      <span>{config.label}</span>
    </Badge>
  );
};

export default EventStatusBadgeShadcn;